/**
 * Client for the staff API (api/src/app.ts), which only admins may call.
 * Same origin: Firebase Hosting rewrites /api/** to the Cloud Run service.
 */
import { getToken } from 'firebase/app-check';
import { trackEvent } from '../analytics';
import { appCheck, auth } from '../firebase';
import { EMAIL_RE, LIMITS, collapseSpaces, type Validated } from './validation';

export type StaffRole = 'staff' | 'admin';

export const STAFF_NAME_MAX = 100;

export interface StaffMember {
  uid: string;
  email: string;
  name: string;
  role: StaffRole | null;
  disabled: boolean;
  twoStep: boolean;
  lastSignIn: string | null;
  created: string | null;
}

export interface StaffInput {
  email: string;
  name: string;
  role: StaffRole | '';
}

export interface StaffChanges {
  name?: string;
  role?: StaffRole | null;
  disabled?: boolean;
}

/** The API refused the request; the message is written for the person at the screen. */
export class StaffApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'StaffApiError';
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const user = auth.currentUser;
  if (!user) throw new StaffApiError(401, 'Sign in again.');

  const headers: Record<string, string> = { Authorization: `Bearer ${await user.getIdToken()}` };
  if (appCheck) headers['X-Firebase-AppCheck'] = (await getToken(appCheck)).token;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(`/api/staff${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new StaffApiError(0, 'No connection. Check the network and try again.');
  }

  if (res.status === 204) return undefined as T;
  // Anything that is not JSON did not come from the API (for example, it is not deployed).
  const data = res.headers.get('Content-Type')?.includes('application/json')
    ? ((await res.json()) as { error?: { message?: string } })
    : null;
  if (!res.ok || !data) {
    throw new StaffApiError(res.status, data?.error?.message ?? 'The staff service is unavailable. Try again later.');
  }
  return data as T;
}

export async function listStaff(): Promise<StaffMember[]> {
  return (await call<{ staff: StaffMember[] }>('GET', '')).staff;
}

export async function addStaff(input: { email: string; name: string; role: StaffRole }): Promise<StaffMember> {
  const { member } = await call<{ member: StaffMember }>('POST', '', input);
  trackEvent('staff_member_added');
  return member;
}

export async function updateStaff(uid: string, changes: StaffChanges): Promise<StaffMember> {
  return (await call<{ member: StaffMember }>('PATCH', `/${encodeURIComponent(uid)}`, changes)).member;
}

export async function deleteStaff(uid: string): Promise<void> {
  await call<void>('DELETE', `/${encodeURIComponent(uid)}`);
}

export async function resetTwoStep(uid: string): Promise<StaffMember> {
  return (await call<{ member: StaffMember }>('POST', `/${encodeURIComponent(uid)}/reset-mfa`)).member;
}

/** A single-use link (valid for 1 hour) that lets the person set their password. */
export async function passwordLink(uid: string): Promise<string> {
  return (await call<{ link: string }>('POST', `/${encodeURIComponent(uid)}/password-link`)).link;
}

export function validateNewStaff(
  input: StaffInput,
): Validated<{ email: string; name: string; role: StaffRole }, StaffInput> {
  const email = input.email.trim().toLowerCase();
  const name = collapseSpaces(input.name);
  const errors: Partial<Record<keyof StaffInput, string>> = {};
  if (!email) errors.email = 'Enter their work email.';
  else if (email.length > LIMITS.email || !EMAIL_RE.test(email)) errors.email = 'Enter a valid email address.';
  if (name.length > STAFF_NAME_MAX) errors.name = `Use at most ${STAFF_NAME_MAX} characters.`;
  if (input.role !== 'staff' && input.role !== 'admin') errors.role = 'Choose a role.';
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return { ok: true, value: { email, name, role: input.role as StaffRole } };
}
